import { useRef } from "react";
import { motion, useMotionValue, useScroll, useSpring, useTransform } from "framer-motion";
import { ArrowDownRight } from "lucide-react";
import { MaskLine, Eyebrow } from "../components/Reveal";
import { scrollToId } from "../lib/api";

const Corner = ({ className }) => <span className={`absolute h-5 w-5 border-[#00FF66] ${className}`} />;

const HeroFrame = () => {
  const mx = useMotionValue(0);
  const my = useMotionValue(0);
  const rx = useSpring(useTransform(my, [-0.5, 0.5], [10, -10]), { stiffness: 120, damping: 18 });
  const ry = useSpring(useTransform(mx, [-0.5, 0.5], [-12, 12]), { stiffness: 120, damping: 18 });
  const move = (e) => {
    const r = e.currentTarget.getBoundingClientRect();
    mx.set((e.clientX - r.left) / r.width - 0.5);
    my.set((e.clientY - r.top) / r.height - 0.5);
  };
  return (
    <motion.div
      initial={{ opacity: 0, scale: 0.9 }}
      animate={{ opacity: 1, scale: 1 }}
      transition={{ duration: 1.4, delay: 0.6, ease: [0.16, 1, 0.3, 1] }}
      className="relative mx-auto w-full max-w-[460px]"
      style={{ perspective: 1200 }}
      onMouseMove={move}
      onMouseLeave={() => { mx.set(0); my.set(0); }}
      data-testid="hero-frame"
    >
      <div className="absolute -inset-16 rounded-full bg-[#00FF66]/20 blur-[90px]" />
      <motion.div style={{ rotateX: rx, rotateY: ry, transformStyle: "preserve-3d" }} className="relative">
        <div className="clip-notch relative aspect-[3/4] overflow-hidden bg-[#18181C]">
          <img src="/img/n5.jpg" alt="Model wearing the T Brand Unleash The Wild oversized tee" className="h-full w-full object-cover" />
          <div className="absolute inset-0 bg-[radial-gradient(circle_at_50%_30%,transparent_30%,rgba(0,0,0,0.65)_100%)]" />
        </div>
        <div style={{ transform: "translateZ(60px)" }} className="absolute -bottom-6 left-2 bg-[#00FF66] px-4 py-3 text-black md:-left-10">
          <p className="font-mono text-[11px] font-bold uppercase tracking-widest">Now dropping</p>
          <p className="font-display text-3xl font-black uppercase leading-none">Wild Tee</p>
        </div>
        <div style={{ transform: "translateZ(40px)" }} className="absolute right-2 top-8 border border-white/20 bg-black/80 px-3 py-2 font-mono text-xs uppercase backdrop-blur-xl md:-right-8">240 GSM</div>
      </motion.div>
      <Corner className="-left-3 -top-3 border-l-2 border-t-2" />
      <Corner className="-right-3 -bottom-3 border-b-2 border-r-2" />
    </motion.div>
  );
};

export const Hero = () => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const y = useTransform(scrollYProgress, [0, 1], [0, 160]);
  const yText = useTransform(scrollYProgress, [0, 1], [0, -80]);

  return (
    <section ref={ref} className="relative min-h-[100svh] overflow-hidden px-5 pb-20 pt-28 md:px-12 md:pt-32" data-testid="hero-section">
      <div className="pointer-events-none absolute inset-0 bg-[linear-gradient(rgba(255,255,255,0.04)_1px,transparent_1px),linear-gradient(90deg,rgba(255,255,255,0.04)_1px,transparent_1px)] bg-[size:80px_80px]" />
      <div className="relative mx-auto grid max-w-[1600px] items-center gap-16 lg:grid-cols-12">
        <motion.div style={{ y: yText }} className="lg:col-span-7">
          <MaskLine delay={0.1}><Eyebrow>Edition 01 · Drop live · India</Eyebrow></MaskLine>
          <h1 className="mt-6 font-display font-black uppercase leading-[0.82] tracking-tighter text-[22vw] sm:text-[17vw] lg:text-[11.5vw]">
            <MaskLine delay={0.2}>Built</MaskLine>
            <MaskLine delay={0.32}><span className="text-outline">for</span> <span className="text-[#00FF66]">the</span></MaskLine>
            <MaskLine delay={0.44} className="italic">Brave.</MaskLine>
          </h1>
          <motion.p initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 1.2, duration: 0.8 }} className="mt-8 max-w-md text-base leading-relaxed text-zinc-300 md:text-lg">
            Heavyweight tees for people who don't whisper. Cut oversized, printed loud, made in limited runs. When it's gone, it's gone.
          </motion.p>
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 1.35, duration: 0.8 }} className="mt-10 flex flex-wrap gap-4">
            <button data-testid="hero-shop-btn" onClick={() => scrollToId("shop")} className="group flex items-center gap-3 bg-[#00FF66] px-7 py-4 font-display text-2xl font-black uppercase text-black transition-colors hover:bg-white">
              Shop Drop 01 <ArrowDownRight className="h-6 w-6 transition-transform group-hover:rotate-[-45deg]" />
            </button>
            <button data-testid="hero-lookbook-btn" onClick={() => scrollToId("lookbook")} className="border border-white/25 px-7 py-4 font-display text-2xl font-black uppercase transition-colors hover:border-[#00FF66] hover:text-[#00FF66]">
              Lookbook
            </button>
          </motion.div>
        </motion.div>
        <motion.div style={{ y }} className="lg:col-span-5">
          <HeroFrame />
        </motion.div>
      </div>
      <div className="absolute bottom-6 left-5 right-5 mx-auto flex max-w-[1600px] justify-between font-mono text-[11px] uppercase tracking-widest text-zinc-400 md:left-12 md:right-12">
        <span>Scroll ↓</span>
        <span className="hidden sm:inline">19.07°N / 72.87°E</span>
        <span>@tbrand_111</span>
      </div>
    </section>
  );
};
